import type { ComponentPropsWithRef } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/shared-ui/atoms';
import { AddToBagIcon } from '@/shared-ui/icons';

export type ProductButtonKind = 'add-to-bag' | 'buy-in-app' | 'notify-me' | 'quick-buy' | 'out-of-stock';

export type ProductButtonProps = Omit<ComponentPropsWithRef<'button'>, 'children'> & {
    kind: ProductButtonKind;
    /** Visible label; also the accessible name in the icon layout. */
    label: string;
    /** Add to Bag is green on the PDP and black on listings (Figma "atb - pdp" / "atb - plp"). */
    context?: 'pdp' | 'plp';
    layout?: 'full' | 'icon';
    /** Full layout only; Figma is 48px (`lg`), compact tiles use `sm`. */
    size?: 'sm' | 'default' | 'lg';
    isLoading?: boolean;
};

const variantFor = (kind: ProductButtonKind, context: 'pdp' | 'plp'): 'purchase' | 'primary' | 'secondary' => {
    switch (kind) {
        case 'add-to-bag':
            return context === 'pdp' ? 'purchase' : 'primary';
        case 'notify-me':
        case 'quick-buy':
            return 'secondary';
        default:
            return 'primary';
    }
};

/**
 * Product CTA from the Figma "Product Buttons" library. Presentational only: callers resolve the
 * kind, label and disabled state (see `src/features/product-button`).
 */
export const ProductButton = ({
    kind,
    label,
    context = 'plp',
    layout = 'full',
    size = 'lg',
    isLoading = false,
    disabled,
    className,
    type = 'button',
    ...props
}: ProductButtonProps) => {
    const isDisabled = Boolean(disabled) || isLoading || kind === 'out-of-stock';

    if (layout === 'icon') {
        return (
            <Button
                type={type}
                variant="primary"
                size="icon"
                disabled={isDisabled}
                aria-label={label}
                aria-busy={isLoading || undefined}
                data-slot="product-button"
                data-kind={kind}
                className={cn('size-10 shrink-0', className)}
                {...props}>
                <AddToBagIcon className="size-5" aria-hidden />
            </Button>
        );
    }

    return (
        <Button
            type={type}
            variant={variantFor(kind, context)}
            size={size}
            disabled={isDisabled}
            aria-busy={isLoading || undefined}
            data-slot="product-button"
            data-kind={kind}
            className={cn('w-full', className)}
            {...props}>
            {label}
        </Button>
    );
};
ProductButton.displayName = 'ProductButton';
